import {
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { ThemeContext, type Theme } from "@/contexts/themeContextDef";

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window !== "undefined") {
      // Validate rather than blind-cast: a corrupted/legacy value must fall back
      // to "light" instead of producing an invalid theme class.
      return localStorage.getItem("bloxida-theme") === "dark"
        ? "dark"
        : "light";
    }
    return "light";
  });

  // Apply theme class to documentElement
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(theme);
    localStorage.setItem("bloxida-theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    const next: Theme = theme === "light" ? "dark" : "light";

    if (typeof window === "undefined") {
      setTheme(next);
      return;
    }

    const doc = window.document;
    const docWithTransition = doc as unknown as { startViewTransition?: (cb: () => void) => void };

    if (typeof docWithTransition.startViewTransition === "function") {
      docWithTransition.startViewTransition(() => {
        setTheme(next);
      });
      return;
    }

    // Fallback: apply temporary transition class
    doc.documentElement.classList.add("theme-transitioning");
    setTheme(next);
    setTimeout(() => {
      doc.documentElement.classList.remove("theme-transitioning");
    }, 400);
  };

  return <ThemeContext value={{ theme, toggleTheme }}>{children}</ThemeContext>;
}
